import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Alert,
  CircularProgress,
  Typography,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { useRegisterCustomer } from '../../hooks/customers/useCustomers';
import { CustomerRegistrationRequest } from '../../api/types/customer.types';
import { useSnackbar } from 'notistack';

interface CustomerRegistrationModalProps {
  open: boolean;
  onClose: () => void;
}

const CustomerRegistrationModal: React.FC<CustomerRegistrationModalProps> = ({ open, onClose }) => {
  const { enqueueSnackbar } = useSnackbar();
  const registerMutation = useRegisterCustomer();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CustomerRegistrationRequest>({
    defaultValues: {
      organizationId: process.env.REACT_APP_ORGANIZATION_ID || '6d402c72-75a9-4d37-840f-b85959f804ff',
      hasActiveFuliza: false,
      gender: 'MALE',
      customerType: 'SALARIED',
    } as Partial<CustomerRegistrationRequest>,
  });

  const onSubmit = async (data: CustomerRegistrationRequest) => {
    try {
      console.log('Submitting customer registration:', data);
      await registerMutation.mutateAsync(data);
      enqueueSnackbar('Customer registered successfully!', { variant: 'success' });
      reset();
      onClose();
    } catch (error) {
      console.error('Registration error:', error);
      enqueueSnackbar(
        error instanceof Error ? error.message : 'Registration failed',
        { variant: 'error' }
      );
    }
  };

  const onError = (errors: any) => { // eslint-disable-line @typescript-eslint/no-explicit-any
    console.error('Form validation errors:', errors);
    enqueueSnackbar('Please fill in all required fields correctly', { variant: 'error' });
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h5" fontWeight="bold">
          Register New Customer
        </Typography>
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit, onError)}>
        <DialogContent>
          {registerMutation.isError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {registerMutation.error instanceof Error
                ? registerMutation.error.message
                : 'Registration failed'}
            </Alert>
          )}

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
            {/* Personal Information */}
            <Controller
              name="fullName"
              control={control}
              rules={{ required: 'Full name is required' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Full Name"
                  required
                  error={!!errors.fullName}
                  helperText={errors.fullName?.message}
                  fullWidth
                />
              )}
            />

            <Controller
              name="idNumber"
              control={control}
              rules={{ required: 'ID number is required' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="ID Number"
                  required
                  error={!!errors.idNumber}
                  helperText={errors.idNumber?.message}
                  fullWidth
                />
              )}
            />

            <Controller
              name="phoneNumber"
              control={control}
              rules={{
                required: 'Phone number is required',
                pattern: {
                  value: /^254[17]\d{8}$/,
                  message: 'Phone must start with 254 and be 12 digits',
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Phone Number"
                  required
                  placeholder="254712345678"
                  error={!!errors.phoneNumber}
                  helperText={errors.phoneNumber?.message}
                  fullWidth
                />
              )}
            />

            <Controller
              name="email"
              control={control}
              rules={{
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address',
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Email"
                  type="email"
                  required
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  fullWidth
                />
              )}
            />

            <Controller
              name="dateOfBirth"
              control={control}
              rules={{ required: 'Date of birth is required' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Date of Birth"
                  type="date"
                  required
                  InputLabelProps={{ shrink: true }}
                  error={!!errors.dateOfBirth}
                  helperText={errors.dateOfBirth?.message}
                  fullWidth
                />
              )}
            />

            <Controller
              name="gender"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Gender"
                  select
                  required
                  fullWidth
                >
                  <MenuItem value="MALE">Male</MenuItem>
                  <MenuItem value="FEMALE">Female</MenuItem>
                  <MenuItem value="OTHER">Other</MenuItem>
                </TextField>
              )}
            />

            <Controller
              name="customerType"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Customer Type"
                  select
                  required
                  fullWidth
                >
                  <MenuItem value="SALARIED">Salaried</MenuItem>
                  <MenuItem value="BUSINESS">Business</MenuItem>
                  <MenuItem value="SELF_EMPLOYED">Self Employed</MenuItem>
                </TextField>
              )}
            />

            <Controller
              name="monthlyIncome"
              control={control}
              rules={{
                required: 'Monthly income is required',
                min: { value: 0, message: 'Must be positive' },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Monthly Income (KES)"
                  type="number"
                  required
                  error={!!errors.monthlyIncome}
                  helperText={errors.monthlyIncome?.message}
                  fullWidth
                />
              )}
            />

            {/* Employment Information */}
            <Controller
              name="employerName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Employer Name"
                  fullWidth
                />
              )}
            />

            <Controller
              name="employmentStartDate"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Employment Start Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              )}
            />

            {/* Address */}
            <Controller
              name="residentialAddress"
              control={control}
              rules={{ required: 'Address is required' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Residential Address"
                  required
                  error={!!errors.residentialAddress}
                  helperText={errors.residentialAddress?.message}
                  fullWidth
                  sx={{ gridColumn: 'span 2' }}
                />
              )}
            />

            <Controller
              name="county"
              control={control}
              rules={{ required: 'County is required' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="County"
                  required
                  error={!!errors.county}
                  helperText={errors.county?.message}
                  fullWidth
                />
              )}
            />

            <Controller
              name="subCounty"
              control={control}
              rules={{ required: 'Sub-county is required' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Sub-County"
                  required
                  error={!!errors.subCounty}
                  helperText={errors.subCounty?.message}
                  fullWidth
                />
              )}
            />

            {/* Financial Information */}
            <Controller
              name="mpesaNumber"
              control={control}
              rules={{
                required: 'M-Pesa number is required',
                pattern: {
                  value: /^254[17]\d{8}$/,
                  message: 'Must start with 254 and be 12 digits',
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="M-Pesa Number"
                  required
                  placeholder="254712345678"
                  error={!!errors.mpesaNumber}
                  helperText={errors.mpesaNumber?.message}
                  fullWidth
                />
              )}
            />

            <Controller
              name="bankName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Bank Name"
                  fullWidth
                />
              )}
            />

            <Controller
              name="bankAccountNumber"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Bank Account Number"
                  fullWidth
                />
              )}
            />

            <Controller
              name="hasActiveFuliza"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Has Active Fuliza?"
                  select
                  fullWidth
                  value={field.value ? 'true' : 'false'}
                  onChange={(e) => field.onChange(e.target.value === 'true')}
                >
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </TextField>
              )}
            />

            {/* Reference Information */}
            <Controller
              name="referenceName"
              control={control}
              rules={{ required: 'Reference name is required' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Reference Name"
                  required
                  error={!!errors.referenceName}
                  helperText={errors.referenceName?.message}
                  fullWidth
                />
              )}
            />

            <Controller
              name="referenceRelationship"
              control={control}
              rules={{ required: 'Relationship is required' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Reference Relationship"
                  required
                  error={!!errors.referenceRelationship}
                  helperText={errors.referenceRelationship?.message}
                  fullWidth
                />
              )}
            />

            <Controller
              name="referencePhone"
              control={control}
              rules={{
                required: 'Reference phone is required',
                pattern: {
                  value: /^254[17]\d{8}$/,
                  message: 'Must start with 254 and be 12 digits',
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Reference Phone"
                  required
                  placeholder="254712345678"
                  error={!!errors.referencePhone}
                  helperText={errors.referencePhone?.message}
                  fullWidth
                  sx={{ gridColumn: 'span 2' }}
                />
              )}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleClose} disabled={registerMutation.isPending}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={registerMutation.isPending}
            startIcon={registerMutation.isPending && <CircularProgress size={20} />}
          >
            {registerMutation.isPending ? 'Registering...' : 'Register Customer'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CustomerRegistrationModal;
